/**
 * lien-favorites.js — Favoriler (wishlist), uygulamasız.
 *
 * Neden localStorage: müşteri hesabı zorunlu değil, üçüncü parti app istemiyoruz.
 * Tek anahtar `lien:favorites` → ürün handle dizisi (JSON). Her erişim try/catch
 * içinde; Safari gizli mod / kapalı depolama sessizce "boş liste" davranır.
 *
 * Özel elemanlar:
 *   <lien-fav-button data-handle data-title>  kalp toggle
 *   <lien-fav-count>                          header rozeti (0 ise gizli)
 *   <lien-favorites-list>                     /pages/favoriler ürün ızgarası
 *
 * Sepete ekleme, temanın kendi product-form.js akışını taklit eder:
 * /cart/add.js + `sections` → CartAddEvent → drawer/rozet kendini günceller.
 */
import { CartAddEvent } from '@theme/events';

const KEY = 'lien:favorites';
const EVT = 'lien:favorites-change';

/** @returns {string[]} */
function read() {
  try {
    const raw = localStorage.getItem(KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr.filter((h) => typeof h === 'string') : [];
  } catch {
    return [];
  }
}

/** @param {string[]} handles */
function write(handles) {
  try {
    localStorage.setItem(KEY, JSON.stringify(handles));
  } catch {
    /* depolama kapalıysa sessizce geç — UI yine de bu sekmede güncellenir */
  }
  document.dispatchEvent(new CustomEvent(EVT, { detail: { handles } }));
}

/** @param {string} handle */
function toggle(handle) {
  const list = read();
  const i = list.indexOf(handle);
  if (i === -1) list.unshift(handle);
  else list.splice(i, 1);
  write(list);
  return i === -1;
}

/** Fiyat: kuruş → "399,00 TL". Shopify.formatMoney her sayfada yok, elle biçimliyoruz. */
function money(cents) {
  return `${(cents / 100).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} TL`;
}

/** Değişiklikleri (bu sekme + diğer sekmeler) tek yerden dinle. */
function onChange(fn) {
  document.addEventListener(EVT, fn);
  window.addEventListener('storage', (e) => {
    if (e.key === KEY || e.key === null) fn();
  });
}

/* ---------------- kalp butonu ---------------- */
class LienFavButton extends HTMLElement {
  connectedCallback() {
    this.btn = this.querySelector('button');
    if (!this.btn) return;
    this.handle = this.dataset.handle || '';
    this.btn.addEventListener('click', this.#onClick);
    onChange(this.#sync);
    this.#sync();
  }

  #onClick = (e) => {
    // Ürün kartında <a> içinde olabilir; tıklama sayfaya gitmesin
    e.preventDefault();
    e.stopPropagation();
    const added = toggle(this.handle);
    if (added) {
      // Küçük "pop": yalnızca transform animasyonu (60fps kuralı)
      this.btn.classList.remove('is-popping');
      void this.btn.offsetWidth; // animasyonu yeniden tetiklemek için reflow
      this.btn.classList.add('is-popping');
    }
  };

  // Ok fonksiyonu: onChange'e referans olarak veriliyor, bind gerekmesin
  #sync = () => {
    const active = read().includes(this.handle);
    const title = this.dataset.title ? ` — ${this.dataset.title}` : '';
    this.btn.classList.toggle('is-active', active);
    this.btn.setAttribute('aria-pressed', String(active));
    this.btn.setAttribute('aria-label', (active ? 'Favorilerden çıkar' : 'Favorilere ekle') + title);
  };
}

/* ---------------- header rozeti ---------------- */
class LienFavCount extends HTMLElement {
  connectedCallback() {
    onChange(this.#sync);
    this.#sync();
  }
  #sync = () => {
    const n = read().length;
    const target = this.querySelector('[data-count]') || this;
    target.textContent = n > 99 ? '99+' : String(n || '');
    this.hidden = n === 0;
  };
}

/* ---------------- favoriler sayfası ---------------- */
class LienFavoritesList extends HTMLElement {
  #lastSig = '';

  connectedCallback() {
    onChange(this.#render);
    this.#render();
  }

  get #cartSectionId() {
    return (
      this.dataset.cartSectionId ||
      document.querySelector('cart-items-component')?.dataset.sectionId ||
      ''
    );
  }

  #render = async () => {
    const handles = read();
    if (handles.length === 0) {
      this.innerHTML = `
        <div class="lfav__empty">
          <p class="lfav__empty-text">${this.dataset.emptyText || 'Henüz favori ürününüz yok.'}</p>
          <a class="lfav__empty-link" href="/collections/all">${this.dataset.emptyLink || 'Ürünleri keşfet'}</a>
        </div>`;
      return;
    }

    // Liste yalnızca küçüldüyse ("Kaldır" / başka sekme) yeniden fetch etme,
    // eksilen kartları DOM'dan düşür — ağ isteği ve yeniden çizim tasarrufu
    const sig = handles.join(',');
    const prev = this.#lastSig ? this.#lastSig.split(',') : [];
    const onlyShrunk = prev.length > 0 && this.querySelector('.lfav__grid') && handles.every((h) => prev.includes(h));
    this.#lastSig = sig;
    if (onlyShrunk) {
      this.querySelectorAll('[data-handle]').forEach((card) => {
        if (!handles.includes(card.dataset.handle)) card.remove();
      });
      return;
    }

    const results = await Promise.allSettled(
      handles.map((h) => fetch(`/products/${h}.js`, { headers: { Accept: 'application/json' } }).then((r) => (r.ok ? r.json() : Promise.reject(r.status))))
    );
    const products = results.filter((r) => r.status === 'fulfilled').map((r) => r.value);

    if (products.length === 0) {
      this.#lastSig = '';
      this.innerHTML = `<div class="lfav__empty"><p class="lfav__empty-text">Favori ürünler şu an görüntülenemiyor.</p><a class="lfav__empty-link" href="/collections/all">Ürünleri keşfet</a></div>`;
      return;
    }

    const grid = document.createElement('div');
    grid.className = 'lfav__grid';
    products.forEach((p) => grid.appendChild(this.#card(p)));
    this.replaceChildren(grid);
  };

  /** @param {any} p — /products/{handle}.js çıktısı */
  #card(p) {
    const variant = p.variants.find((v) => v.available) || p.variants[0];
    const img = p.featured_image ? `${p.featured_image}${p.featured_image.includes('?') ? '&' : '?'}width=640` : '';
    const hasCompare = variant && variant.compare_at_price > variant.price;
    const el = document.createElement('article');
    el.className = 'lfav__card';
    el.dataset.handle = p.handle;
    el.innerHTML = `
      <a class="lfav__media" href="/products/${p.handle}" aria-label="${esc(p.title)}">
        ${img ? `<img src="${esc(img)}" alt="" loading="lazy" width="640" height="800">` : ''}
      </a>
      <div class="lfav__body">
        <h3 class="lfav__title"><a href="/products/${p.handle}">${esc(p.title)}</a></h3>
        <p class="lfav__price">
          ${hasCompare ? `<s class="lfav__compare">${money(variant.compare_at_price)}</s>` : ''}
          <span>${variant ? money(variant.price) : ''}</span>
        </p>
        <div class="lfav__actions">
          <button type="button" class="lfav__add" data-variant="${variant?.id || ''}" ${variant?.available ? '' : 'disabled'}>
            <span>${variant?.available ? 'Sepete Ekle' : 'Tükendi'}</span>
          </button>
          <button type="button" class="lfav__remove" aria-label="${esc(p.title)} favorilerden kaldır">Kaldır</button>
        </div>
      </div>`;

    el.querySelector('.lfav__remove').addEventListener('click', () => {
      // Önce görsel olarak düşür (opacity+transform), sonra depodan sil
      el.classList.add('is-leaving');
      const done = () => {
        const list = read().filter((h) => h !== p.handle);
        write(list);
      };
      if (matchMedia('(prefers-reduced-motion: reduce)').matches) done();
      else el.addEventListener('transitionend', done, { once: true });
    });

    el.querySelector('.lfav__add').addEventListener('click', (e) => this.#addToCart(e.currentTarget, p));
    return el;
  }

  /** @param {HTMLButtonElement} btn @param {any} p */
  async #addToCart(btn, p) {
    const id = Number(btn.dataset.variant);
    if (!id || btn.disabled) return;
    const label = btn.querySelector('span');
    btn.disabled = true;
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id, quantity: 1 }], sections: this.#cartSectionId }),
      });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || 'Sepete eklenemedi');

      // Temanın product-form.js'i ile aynı imza: drawer + rozet bu event'i dinler
      document.dispatchEvent(
        new CartAddEvent({}, String(id), {
          source: 'lien-favorites',
          itemCount: 1,
          productId: String(p.id),
          variantId: String(id),
          sections: data.sections,
        })
      );
      label.textContent = 'Eklendi ✓';
      btn.classList.add('is-added');
    } catch (err) {
      console.error('[lien-favorites]', err);
      label.textContent = 'Hata, tekrar dene';
    }
    setTimeout(() => {
      label.textContent = 'Sepete Ekle';
      btn.classList.remove('is-added');
      btn.disabled = false;
    }, 1500);
  }
}

/** HTML kaçış — ürün başlıkları innerHTML'e giriyor */
function esc(s) {
  return String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
}

if (!customElements.get('lien-fav-button')) customElements.define('lien-fav-button', LienFavButton);
if (!customElements.get('lien-fav-count')) customElements.define('lien-fav-count', LienFavCount);
if (!customElements.get('lien-favorites-list')) customElements.define('lien-favorites-list', LienFavoritesList);
